import { ArrowDown, ArrowRight, Check, GitBranch, LockKeyhole, RefreshCw, ScanSearch, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { getSystemChainColumns, getSystemChainLayout } from "../../shared/system-chain-layout";
import type { SystemVisual, SystemVisualNode, SystemVisualNodeType } from "../../shared/types";
import { api } from "../api";

const nodeIcons: Record<SystemVisualNodeType, typeof ScanSearch> = {
  system: ScanSearch,
  review: GitBranch,
  gate: LockKeyhole,
  output: Check,
  route: GitBranch,
};

function FlowNode({ node }: { node: SystemVisualNode }) {
  const Icon = nodeIcons[node.type];
  return <article className={`system-node system-node--${node.type}`}>
    <div className="system-node__heading">
      <div className="system-node__mark" aria-hidden="true"><Icon size={16} /></div>
      <h3>{node.title}</h3>
    </div>
    <p>{node.description}</p>
  </article>;
}

function SystemChain({ steps }: { steps: SystemVisualNode[] }) {
  const chainRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);
  const measure = useCallback(() => {
    if (!chainRef.current) return;
    setColumns(getSystemChainColumns(chainRef.current.clientWidth, steps.length));
  }, [steps.length]);

  useLayoutEffect(() => {
    measure();
    if (!chainRef.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(chainRef.current);
    return () => observer.disconnect();
  }, [measure]);

  const layout = getSystemChainLayout(steps.length, columns);
  return <div
    className="system-chain"
    data-columns={columns}
    ref={chainRef}
    style={{ gridTemplateColumns: `repeat(${columns}, minmax(240px, 286px))` }}
  >
    {steps.map((step, index) => {
      const position = layout[index];
      if (!position) return null;
      return <div
        className="system-chain__item"
        key={step.id}
        style={{ gridColumn: position.column, gridRow: position.row }}
      >
        <FlowNode node={step} />
        {position.direction && <ArrowRight className={`system-arrow system-arrow--${position.direction}`} size={17} aria-hidden="true" />}
      </div>;
    })}
  </div>;
}

export function SystemVisualPage() {
  const [visual, setVisual] = useState<SystemVisual | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try { setVisual(await api.systemVisual()); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "The system map could not be loaded"); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!visual && !error) return <div className="system-visual-page system-visual-loading" aria-label="Loading System Visual"><span /><span /><span /></div>;
  if (!visual) return <div className="page-error"><h1>System Visual unavailable</h1><p>{error}</p><button className="secondary-button" onClick={() => void load()}><RefreshCw size={16} />Try again</button></div>;

  const verified = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(visual.verified_at));
  return <div className="system-visual-page">
    <header className="system-visual-header">
      <div className="system-visual-header__copy">
        <span className="eyebrow">Living operating map</span><h1>{visual.title}</h1><p>{visual.subtitle}</p>
        <div className="system-legend" aria-label="System Visual legend">
          <span>Reading key</span>
          {visual.legend.map((item) => <div key={item.type}><i className={`system-legend__mark system-legend__mark--${item.type}`} />{item.label}</div>)}
        </div>
      </div>
      <dl className="system-visual-meta">
        <div><dt>Status</dt><dd><ShieldCheck size={14} />Verified</dd></div>
        <div><dt>Workflow</dt><dd>{visual.workflow_version}</dd></div>
        <div><dt>Last reconciled</dt><dd>{verified}</dd></div>
        <div><dt>Authority</dt><dd>{visual.source_files.length} sources</dd></div>
      </dl>
    </header>

    <section className="system-manifest" aria-labelledby="system-manifest-title">
      <div className="system-manifest__mark" aria-hidden="true">◆</div>
      <div><h2 id="system-manifest-title">{visual.manifest.title}</h2><p>{visual.manifest.description}</p></div>
      <code title={visual.source_fingerprint}>{visual.source_fingerprint.slice(0, 12)}</code>
    </section>

    <section className="system-inputs" aria-label="Workflow inputs">
      {visual.inputs.map((input, index) => <div className="system-input-wrap" key={input.id}>
        {index > 0 && <span className="system-input-plus" aria-hidden="true">+</span>}
        <article className="system-input"><span>Input {String(index + 1).padStart(2, "0")}</span><h2>{input.title}</h2><p>{input.description}</p></article>
      </div>)}
    </section>

    <div className="system-down" aria-hidden="true"><ArrowDown size={18} /></div>

    <div className="system-phases">
      {visual.phases.map((phase, phaseIndex) => <div key={phase.id}>
        <section className={`system-phase system-phase--${phase.layout}`} aria-labelledby={`system-phase-${phase.id}`}>
          <header className="system-phase__header"><span>{phase.id}</span><h2 id={`system-phase-${phase.id}`}>{phase.title}</h2><p>{phase.summary}</p></header>
          <div className="system-phase__flow">
            <SystemChain steps={phase.steps} />
            {phase.routes && <div className="system-routes" aria-label="Visual unit execution routes">
              <div className="system-routes__label"><GitBranch size={16} /><span>Choose one approved route per visual unit</span></div>
              <div className="system-routes__grid">{phase.routes.map((route) => <FlowNode key={route.id} node={route} />)}</div>
            </div>}
            {phase.loop_label && <div className="system-loop"><RefreshCw size={15} aria-hidden="true" /><span>{phase.loop_label}</span></div>}
          </div>
        </section>
        {phaseIndex < visual.phases.length - 1 && <div className="system-down" aria-hidden="true"><ArrowDown size={18} /></div>}
      </div>)}
    </div>

    <footer className="system-visual-footer">Reconciled from authoritative project sources whenever the exact <code>update</code> command runs.</footer>
  </div>;
}
